import {
  WorkflowNodeType,
  type WorkflowCondition,
  type WorkflowTrigger,
} from '@/gen/cortado/v1/cortado_pb'

import { formatWorkflowTrigger } from '../format-conditions'
import { humanizeVariablePath } from '../variable-picker'
import { humanizeWaitDuration } from '../wait-duration'
import { describeEventOffset, isEventStartAnchor } from '../event-timing'
import type { BuilderNode } from './graph-types'

export type NodeDisplay = {
  title: string
  subtitle: string
}

/**
 * nodeDisplay computes the title + subtitle shown for a node on the canvas and
 * in the form-mode list. The trigger borrows the existing trigger formatter;
 * the rest summarize their typed config. An unconfigured node falls back to a
 * "Configure …" prompt so empty nodes read clearly.
 *
 * `triggerFilterSummary` is the precomputed entry-filter clause
 * (formatTriggerFilterSummary — "Every time" / "Only if …") that completes the
 * trigger's sentence. Passed in rather than derived here so the display stays a
 * pure formatter and callers can source it from the editor context. Omitted
 * (e.g. outside the builder) the trigger keeps its generic subtitle.
 */
export function nodeDisplay(
  node: BuilderNode,
  trigger?: WorkflowTrigger,
  triggerFilterSummary?: string
): NodeDisplay {
  switch (node.config.case) {
    case 'wait':
      if (isEventStartAnchor(node.config.value.anchor)) {
        return {
          title: `Wait until ${describeEventOffset(node.config.value.eventOffsetDays ?? 0)}`,
          subtitle: 'Follows the event date if it moves',
        }
      }
      return {
        title: node.config.value.duration
          ? `Wait ${humanizeWaitDuration(node.config.value.duration)}`
          : 'Wait',
        subtitle: 'Pause before the next step',
      }
    case 'sendTemplatedEmail': {
      const v = node.config.value
      // toField is a dot-path (humanize to "Contact email"); toEmail is a
      // literal address, shown as-is.
      const recipient = v.toField ? humanizeVariablePath(v.toField) : v.toEmail
      return {
        title: recipient ? `Email to ${recipient}` : 'Send templated email',
        subtitle: v.templateName
          ? `Template: ${v.templateName}`
          : v.subjectTemplate || 'No subject yet',
      }
    }
    case 'createTask': {
      const v = node.config.value
      return {
        title: v.title ? `Task: ${v.title}` : 'Create task',
        subtitle: isEventStartAnchor(v.dueAnchor)
          ? `Due ${describeEventOffset(v.dueEventOffsetDays ?? 0)}`
          : `Due in ${v.dueInDays} ${v.dueInDays === 1 ? 'day' : 'days'}`,
      }
    }
    case 'updateField': {
      const v = node.config.value
      return {
        title: v.field ? `Set ${humanizeField(v.field)}` : 'Update field',
        subtitle: v.value ? `to ${v.value}` : 'No value yet',
      }
    }
    case 'sendWebhook':
      return {
        title: 'Send webhook',
        subtitle: node.config.value.endpointId ? 'Signed POST to your endpoint' : 'Choose an endpoint',
      }
    case 'branch':
      return {
        title: 'Branch',
        subtitle: branchSubtitle(node),
      }
    case 'switch': {
      const configuredCases = node.config.value.cases.length
      return {
        title: node.config.value.field
          ? `Switch on ${humanizeField(node.config.value.field)}`
          : 'Switch',
        subtitle: `${configuredCases} ${configuredCases === 1 ? 'case' : 'cases'} + Otherwise`,
      }
    }
    default:
      // Structural nodes (trigger, stop) handled by type below.
      break
  }

  switch (node.type) {
    case WorkflowNodeType.TRIGGER:
      return {
        title: formatWorkflowTrigger(trigger),
        subtitle: triggerFilterSummary ?? 'Starts this workflow',
      }
    case WorkflowNodeType.STOP:
      return {
        title: 'Stop',
        subtitle: 'Ends the workflow run',
      }
    default:
      return { title: 'Step', subtitle: '' }
  }
}

function branchSubtitle(node: BuilderNode): string {
  if (node.config.case !== 'branch') return ''
  const count = countConditionLeaves(node.config.value.condition)
  if (count === 0) return 'Add a condition'
  return `${count} condition${count === 1 ? '' : 's'}`
}

// countConditionLeaves totals the leaf comparisons in a condition tree,
// recursing through nested groups (the builder authors at most two levels, but
// an imported/installed condition may nest deeper). Counting from the proto
// rather than the one-level deserialized form keeps the subtitle accurate at
// any depth.
function countConditionLeaves(condition: WorkflowCondition | undefined): number {
  if (!condition || condition.kind.case === undefined) return 0
  if (condition.kind.case === 'leaf') return 1
  return condition.kind.value.conditions.reduce(
    (sum, child) => sum + countConditionLeaves(child),
    0
  )
}

function humanizeField(field: string): string {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
